import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';

import { m } from 'motion/react';
import { Check } from 'lucide-react';

import { departments, employeeTemplates, employees, teams } from '../../api/organization';
import { Button } from '../../components/Button';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Field } from '../../components/Field';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { RISE, STAGGER } from '../../components/motion';
import { useFormat } from '../../i18n/format';
import { useT } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useSubmit } from '../auth/useSubmit';
import { useCompanyId, useCompanySlug } from '../company/useCompany';

/**
 * Los roles desde los que esta empresa puede contratar —los doce de `04 §9` y
 * los que se haya escrito ella— y los cuatro campos que hacen falta para
 * contratar uno.
 *
 * `estimated_tokens` se enseña junto a cada rol porque es la única cifra que
 * predice lo que cuesta ejecutar a un empleado antes de que haya ejecutado
 * nada: el prompt de sistema se envía en cada turno. El catálogo mide los doce
 * contra la misma empresa inventada, que es lo que los hace comparables.
 */
export function HireFromTemplate({ onClose }: { onClose: () => void }) {
  const t = useT();
  const format = useFormat();
  const companyId = useCompanyId();
  const companySlug = useCompanySlug();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const { busy, error, fields, submit } = useSubmit<{ slug: string }>();

  // El catálogo **de la empresa**, no el de la plataforma: los doce de `04 §9`
  // más las que esta empresa se ha escrito con el asistente. Preguntar por
  // `/catalog/employee-templates` dejaba una plantilla recién aceptada guardada
  // en la base de datos y ausente del único sitio donde se contrata desde ella.
  // La clave es la que invalida el creador al aceptar, que es lo que hace que
  // aparezca sin recargar.
  const templates = useQuery({
    queryKey: ['employee-templates', companyId],
    queryFn: () => employeeTemplates.list(companyId),
    enabled: companyId !== '',
  });

  const tree = useQuery({
    queryKey: ['departments', companyId],
    queryFn: () => departments.list(companyId),
    enabled: companyId !== '',
  });

  const teamList = useQuery({
    queryKey: ['teams', companyId],
    queryFn: () => teams.list(companyId),
    enabled: companyId !== '',
  });

  const roster = useQuery({
    queryKey: ['employees', companyId, 'roster'],
    queryFn: () => employees.list(companyId, new URLSearchParams({ limit: '100' })),
    enabled: companyId !== '',
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const optional = (key: string) => {
      const value = String(form.get(key) ?? '');

      return value === '' ? null : value;
    };

    void submit(
      () =>
        employees.fromTemplate(companyId, {
          template_key: selected,
          name: String(form.get('name') ?? ''),
          department_id: optional('department_id'),
          team_id: optional('team_id'),
          reports_to_employee_id: optional('reports_to_employee_id'),
        }),
      (created) => {
        void queryClient.invalidateQueries({ queryKey: ['employees', companyId] });
        void queryClient.invalidateQueries({ queryKey: ['org-chart', companyId] });
        void navigate({
          to: '/c/$companySlug/employees/$employeeSlug',
          params: { companySlug, employeeSlug: created.slug },
        });
      },
    );
  };

  if (templates.isPending) {
    return <SkeletonRows />;
  }

  if (templates.error) {
    return <ErrorBanner error={templates.error} onRetry={() => void templates.refetch()} />;
  }

  return (
    <m.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4 rounded-lg border border-line bg-raised p-5 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-ink">{t('employees.hire.title')}</h3>
        <Button variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </div>

      <m.ul variants={STAGGER} initial="hidden" animate="shown" className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {templates.data.data.map((template) => (
          <m.li key={template.key} variants={RISE}>
            <button
              type="button"
              onClick={() => setSelected(template.key)}
              aria-pressed={selected === template.key}
              className={cn(
                'relative h-full w-full rounded-md border p-3.5 text-left transition-[border-color,box-shadow,transform] duration-fast hover:-translate-y-px hover:shadow-sm',
                selected === template.key
                  ? 'border-primary bg-primary-pale shadow-sm'
                  : 'border-line bg-paper hover:border-primary/45',
              )}
            >
              {/* La marca de selección entra con muelle: en una rejilla de doce
                  tarjetas, un borde que cambia de color no siempre se ve. */}
              {selected === template.key ? (
                <m.span
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                  className="absolute top-2.5 right-2.5 grid size-5 place-items-center rounded-full bg-primary text-white"
                  aria-hidden="true"
                >
                  <Check className="size-3" strokeWidth={3} />
                </m.span>
              ) : null}

              <p className="pr-6 text-sm font-semibold text-ink">{template.label}</p>
              {/* De dónde sale, y solo cuando no es de las doce: una etiqueta
                  en cada tarjeta sería ruido, y en la que la empresa escribió
                  es la diferencia entre reconocerla y no encontrarla. */}
              {template.origin === 'company' ? (
                <span className="mt-1 inline-block rounded-xs bg-primary-pale px-1.5 py-0.5 text-[0.6rem] font-semibold text-primary">
                  {t('employees.hire.ownTemplate')}
                </span>
              ) : null}
              <p className="mt-1 text-xs leading-relaxed text-ink-soft">{template.description}</p>
              <p className="mt-2.5 font-mono text-[0.62rem] text-ink-faint">
                {template.model} · {template.reasoning_effort} ·{' '}
                {t('employees.hire.tokens', { count: format.number(template.estimated_tokens) })}
              </p>
            </button>
          </m.li>
        ))}
      </m.ul>

      {selected !== null ? (
        <form onSubmit={onSubmit} className="max-w-lg space-y-4 border-t border-line pt-4">
          {error ? <ErrorBanner error={error} /> : null}

          <Field
            label={t('employees.hire.name')}
            name="name"
            required
            error={fields['name']}
            hint={t('employees.hire.nameHint')}
          />

          <div className="space-y-1.5">
            <label htmlFor="hire-department" className="block text-sm font-semibold text-ink">
              {t('employees.hire.department')}
            </label>
            <select id="hire-department" name="department_id" className={cn(fieldStyles, 'border-line')}>
              <option value="">{t('common.none')}</option>
              {(tree.data?.data ?? []).map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="hire-team" className="block text-sm font-semibold text-ink">
              {t('employees.hire.homeTeam')}
            </label>
            <select id="hire-team" name="team_id" className={cn(fieldStyles, 'border-line')}>
              <option value="">{t('common.none')}</option>
              {(teamList.data?.data ?? []).map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="hire-manager" className="block text-sm font-semibold text-ink">
              {t('employees.hire.reportsTo')}
            </label>
            <select id="hire-manager" name="reports_to_employee_id" className={cn(fieldStyles, 'border-line')}>
              <option value="">{t('employees.hire.nobodyTop')}</option>
              {(roster.data?.data ?? []).map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name} · {employee.job_title}
                </option>
              ))}
            </select>
            {fields['reports_to_employee_id'] ? (
              <p role="alert" className="animate-rise text-xs font-semibold text-danger">
                {fields['reports_to_employee_id']}
              </p>
            ) : null}
          </div>

          <Button type="submit" busy={busy}>
            {t('employees.hire.submit')}
          </Button>
        </form>
      ) : null}
    </m.div>
  );
}
